'use client'

import { CalculatorValues } from "./types";
import { DEFAULT_CALCULATOR_VALUES } from "./constants";

/**
 * Encodes calculator values into a compressed query string
 */
export function encodeCalculatorValues(values: CalculatorValues): string {
  // Create a new object with only the values that differ from defaults
  const nonDefaultValues = removeDefaultValues(values);
  
  // Convert to JSON and encode
  const jsonString = JSON.stringify(nonDefaultValues);
  return btoa(encodeURIComponent(jsonString));
}

/**
 * Decodes a query string back into calculator values
 */
export function decodeCalculatorValues(encodedString: string): CalculatorValues | null {
  try {
    // Decode the base64 string and parse as JSON
    const jsonString = decodeURIComponent(atob(encodedString));
    const decodedValues = JSON.parse(jsonString);

    // Merge with default values
    return { ...DEFAULT_CALCULATOR_VALUES, ...decodedValues };
  } catch (error) {
    console.error("Error decoding calculator values:", error);
    return null;
  }
}

/**
 * Creates a shareable URL with current calculator values
 */
export function createShareableUrl(values: CalculatorValues): string {
  if (typeof window === 'undefined') {
    return '';
  }
  // Don't create sharing links for default values
  if (isMatchingDefaults(values)) {
    return window.location.href.split('?')[0]; // Return URL without query params
  }
  
  const encodedValues = encodeCalculatorValues(values);

  const url = new URL(window.location.href.split('?')[0]);
  url.searchParams.set('tax', encodedValues);
  
  return url.toString();
}

/**
 * Parse calculator values from URL query parameters
 */
export function parseUrlParams(): CalculatorValues | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const taxParam = urlParams.get('tax');
  
  if (!taxParam) {
    return null;
  }
  
  return decodeCalculatorValues(taxParam);
}

/**
 * Helper function to check if values match defaults
 */
function isMatchingDefaults(values: CalculatorValues): boolean {
  // Short-circuit for common fields
  if (
    values.income !== DEFAULT_CALCULATOR_VALUES.income ||
    values.employmentType !== DEFAULT_CALCULATOR_VALUES.employmentType ||
    values.bonusIncome !== DEFAULT_CALCULATOR_VALUES.bonusIncome ||
    values.deductions !== DEFAULT_CALCULATOR_VALUES.deductions ||
    values.hasHELP !== DEFAULT_CALCULATOR_VALUES.hasHELP ||
    values.hasPrivateHealth !== DEFAULT_CALCULATOR_VALUES.hasPrivateHealth
  ) {
    return false;
  }
  
  // Deep comparison
  const valuesJson = JSON.stringify(removeDefaultValues(values));
  return valuesJson === '{}';
}

/**
 * Helper function to remove default values from object
 */
function removeDefaultValues(values: CalculatorValues): Partial<CalculatorValues> {
  const result: Partial<CalculatorValues> = {};
  
  // Check each key in values and only add it to result if it differs from default
  for (const key in values) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      const typedKey = key as keyof CalculatorValues;
      if (values[typedKey] !== DEFAULT_CALCULATOR_VALUES[typedKey]) {
        // Need to cast to any to avoid TypeScript error with the complex CalculatorValues type
        (result as any)[typedKey] = values[typedKey];
      }
    }
  }
  
  return result;
}